/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Sharing from 'expo-sharing';
import * as ImagePicker from 'expo-image-picker';
import * as Print from 'expo-print';
import * as Clipboard from 'expo-clipboard';
import {
  SafeAreaView, Modal,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  TextInput,
  Image,
  Dimensions,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { useAI, AIChatWidget } from '../../../services/AIBrainService';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
  AntDesign,
} from '@expo/vector-icons';

const { width } = Dimensions.get('window');

// JOBENG Logo component using the app icon
const CompanyLogo = ({ uri, size = 32 }) => {
  if (uri) {
    return (
      <Image
        source={{ uri }}
        style={{ width: size, height: size, borderRadius: 6 }}
        resizeMode="contain"
      />
    );
  }
  return (
    <View style={{ width: size, height: size, borderRadius: 6, backgroundColor: 'rgba(255,255,255,0.2)', alignItems: 'center', justifyContent: 'center' }}>
      <MaterialCommunityIcons name="office-building" size={size * 0.6} color="#fff" />
    </View>
  );
};

// Format options component
const FormatOption = ({ icon, label, selected, onPress }) => (
  <TouchableOpacity
    style={[styles.formatOption, selected && styles.selectedFormatOption]}
    onPress={onPress}
  >
    <MaterialCommunityIcons
      name={icon}
      size={24}
      color={selected ? '#fff' : '#666'}
    />
    <Text
      style={[
        styles.formatOptionText,
        selected && styles.selectedFormatOptionText,
      ]}
    >
      {label}
    </Text>
  </TouchableOpacity>
);

// Color picker component
const ColorPicker = ({ colors, selectedColor, onSelectColor }) => (
  <View style={styles.colorPickerContainer}>
    {colors.map((color, index) => (
      <TouchableOpacity
        key={`color-${index}`}
        style={[
          styles.colorOption,
          { backgroundColor: color },
          selectedColor === color && styles.selectedColorOption,
        ]}
        onPress={() => onSelectColor(color)}
      />
    ))}
  </View>
);

const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const getH = async () => {
  const tok = await AsyncStorage.getItem('auth_token').catch(() => null);
  return { 'Content-Type': 'application/json', 'x-api-key': API_KEY, ...(tok ? { Authorization: `Bearer ${tok}` } : {}) };
};

const BlogEditorScreen = ({ navigation, route }) => {
  const campaignId = route.params?.campaignId || null;
  const campaign   = route.params?.campaign   || null;
  const [companyLogo, setCompanyLogo] = useState(null);

  useEffect(() => {
    (async () => {
      // Load company profile
      try {
        const raw = await AsyncStorage.getItem('company_profile');
        if (raw) {
          const p = JSON.parse(raw);
          const logo = p.logo || p.logo_url || p.company_logo;
          if (logo) setCompanyLogo(logo.startsWith('http') ? logo : `https://api.jobeng.ai${logo}`);
          setCompanyName(p.name || p.company_name || '');
          setCompanyEmail(p.email || '');
        }
      } catch {}
      // Also fetch from API
      try {
        const bid = parseInt(await AsyncStorage.getItem('company_id').catch(()=>'0'))||0;
        const h = await getH();
        const pr = await fetch(`${API_BASE}/api/company-profile/${bid}`,{headers:h}).then(r=>r.json()).catch(()=>null);
        if (pr) { const p=pr.data||pr; if(p.logo)setCompanyLogo(p.logo.startsWith('http')?p.logo:`${API_BASE}${p.logo}`); if(p.name||p.company_name)setCompanyName(p.name||p.company_name); if(p.email)setCompanyEmail(p.email); }
      } catch {}
      // Load drafts
      try {
        const bid = await AsyncStorage.getItem('company_id').catch(()=>'0');
        const h = await getH();
        const dr = await fetch(`${API_BASE}/api/data/blog_posts?company_id=${parseInt(bid)||0}&status=draft&limit=20`,{headers:h}).then(r=>r.json()).catch(()=>null);
        if (dr?.success && Array.isArray(dr.data)) setDrafts(dr.data);
      } catch {}
    })();
  }, []);

  // Word/char count
  useEffect(() => {
    const words = blogContent.trim() ? blogContent.trim().split(/\s+/).length : 0;
    setWordCount(words);
    setCharCount(blogContent.length);
    setReadTime(Math.max(1, Math.ceil(words / 200)));
    if (!slug && blogTitle) setSlug(blogTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''));
  }, [blogContent, blogTitle]);

  const [isLoading, setIsLoading] = useState(false);

  const { ask: askAI, agents: aiAgents, loading: aiLoading, history: aiHistory } = useAI('BlogEditorScreen');
  const [showAIChat, setShowAIChat] = useState(false);

  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('Edit');
  const [blogTitle, setBlogTitle] = useState(route.params?.title || '');
  const [blogContent, setBlogContent] = useState(route.params?.content || '');
  const [selectedCategory, setSelectedCategory] = useState(
    route.params?.category || 'Engineering'
  );
  const [featuredImage, setFeaturedImage] = useState(
    route.params?.featuredImage || null
  );
  const [tags, setTags] = useState(route.params?.tags || []);
  const [currentTag, setCurrentTag] = useState('');
  const [selectedFormat, setSelectedFormat] = useState('Normal');
  const [selectedColor, setSelectedColor] = useState('#333333');
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [focusKeyword, setFocusKeyword] = useState('');
  const [visibility, setVisibility] = useState('public');
  const [scheduleDate, setScheduleDate] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [slug, setSlug] = useState('');
  const [readTime, setReadTime] = useState(0);
  const [wordCount, setWordCount] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const [drafts, setDrafts] = useState([]);
  const [autoSaveTimer, setAutoSaveTimer] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiMode, setAiMode] = useState('write');
  const [aiTone, setAiTone] = useState('professional');
  const [aiLength, setAiLength] = useState('medium');
  const [aiResult, setAiResult] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [isPublished, setIsPublished] = useState(
    route.params?.isPublished || false
  );

  // Format options
  const formatOptions = [
    { icon: 'format-bold', label: 'Bold' },
    { icon: 'format-italic', label: 'Italic' },
    { icon: 'format-underline', label: 'Underline' },
    { icon: 'format-list-bulleted', label: 'List' },
    { icon: 'format-header-1', label: 'Heading' },
    { icon: 'format-quote-open', label: 'Quote' },
    { icon: 'link', label: 'Link' },
    { icon: 'code-tags', label: 'Code' },
    { icon: 'image', label: 'Image' },
    { icon: 'minus', label: 'Divider' },
  ];

  // Category options
  const categoryOptions = [
    'Engineering','Construction','Technology','Sustainability',
    'Industry News','Career Advice','Projects','Innovation',
    'Health & Safety','Recruitment','Case Study','Opinion',
    'Tutorial','Company News','Regulation','Events',
  ];

  // Color options
  const colorOptions = [
    '#333333', // Black
    '#0e4e96', // JOBENG Blue
    '#fdb913', // Canary Yellow
    '#19abb5', // Mint Green
    '#e74c3c', // Red
    '#9b59b6', // Purple
    '#3498db', // Light Blue
    '#2ecc71', // Green
  ];

  // Handle adding a tag
  const handleAddTag = () => {
    if (currentTag.trim() && !tags.includes(currentTag.trim())) {
      setTags([...tags, currentTag.trim()]);
      setCurrentTag('');
    }
  };

  // Handle removing a tag
  const handleRemoveTag = tagToRemove => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  // Handle save blog — real API
  const handleSaveBlog = async (publish = false) => {
    if (!blogTitle.trim()) { Alert.alert('Error', 'Please enter a blog title'); return; }
    if (!blogContent.trim()) { Alert.alert('Error', 'Please enter blog content'); return; }
    setIsSaving(true);
    try {
      const h = await getH();
      const bid = await AsyncStorage.getItem('company_id').catch(() => '0');
      const payload = {
        company_id: parseInt(bid)||0,
        campaign_id: campaignId || null,
        title:       blogTitle.trim(),
        content:     blogContent.trim(),
        category:    selectedCategory,
        tags:        tags,
        status:      publish ? 'published' : 'draft',
        featured_image: featuredImage || null,
        is_published: publish,
      };
      const existing = route.params?.blogId;
      const res = await fetch(
        existing ? `${API_BASE}/api/data/blog_posts/${existing}` : `${API_BASE}/api/data/blog_posts`,
        { method: existing ? 'PUT' : 'POST', headers: h, body: JSON.stringify(payload) }
      );
      const json = await res.json().catch(() => ({}));
      setIsPublished(publish);
      // Cache for campaign content library
      if (campaignId) {
        const key = `campaign_content_${campaignId}`;
        const arr = JSON.parse(await AsyncStorage.getItem(key).catch(() => '[]') || '[]');
        arr.unshift({ type:'blog', title:blogTitle, status:publish?'published':'draft', created_at:new Date().toISOString(), id: json?.data?.id || `blog_${Date.now()}` });
        await AsyncStorage.setItem(key, JSON.stringify(arr.slice(0,50)));
      }
      Alert.alert(
        publish ? '✅ Blog Published' : '✅ Blog Saved',
        publish ? 'Your blog is now live!' : 'Saved as draft.',
        [{ text: 'OK', onPress: () => navigation.goBack() }]
      );
    } catch (e) {
      Alert.alert('Error', e.message);
    } finally { setIsSaving(false); }
  };

  // Handle select featured image
  const handleSelectFeaturedImage = async () => {
    try {
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== 'granted') { Alert.alert('Permission Required','Allow access to photos'); return; }
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [16, 9],
        quality: 0.8,
      });
      if (!result.canceled && result.assets?.[0]) {
        setFeaturedImage(result.assets[0].uri);
      }
    } catch (e) { Alert.alert('Error', e.message); }
  };

  // ═══ Format text helpers ═══
  const insertFormat = (prefix, suffix) => {
    const sel = blogContent;
    setBlogContent(sel + prefix + 'text' + (suffix||''));
  };

  const handleFormat = (label) => {
    setSelectedFormat(label);
    switch(label) {
      case 'Bold': insertFormat('**', '**'); break;
      case 'Italic': insertFormat('_', '_'); break;
      case 'Underline': insertFormat('<u>', '</u>'); break;
      case 'List': setBlogContent(blogContent + '\n- '); break;
      case 'Heading': setBlogContent(blogContent + '\n## '); break;
      case 'Quote': setBlogContent(blogContent + '\n> '); break;
      case 'Link': setBlogContent(blogContent + '[link text](https://)'); break;
      case 'Code': insertFormat('`', '`'); break;
      case 'Image': setBlogContent(blogContent + '\n![alt text](image-url)'); break;
      case 'Divider': setBlogContent(blogContent + '\n---\n'); break;
    }
  };

  // ═══ Export to PDF ═══
  const handleExportPDF = async () => {
    if (!blogTitle || !blogContent) { Alert.alert('Error','Add title and content first'); return; }
    try {
      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><style>
        body{font-family:Arial,sans-serif;color:#1E293B;padding:40px;line-height:1.6}
        h1{color:#0e4e96;font-size:28px;margin-bottom:8px}
        .meta{color:#94A3B8;font-size:12px;margin-bottom:20px}
        .cat{background:#0e4e96;color:#fff;padding:3px 10px;border-radius:12px;font-size:11px;display:inline-block}
        .content{font-size:14px;white-space:pre-wrap}
        .tags{margin-top:20px;color:#64748B;font-size:11px}
        .footer{margin-top:30px;border-top:1px solid #E2E8F0;padding-top:10px;font-size:10px;color:#94A3B8;text-align:center}
      </style></head><body>
        ${companyLogo?`<img src="${companyLogo}" style="width:50px;height:50px;border-radius:10px;margin-bottom:10px"/>`:''}
        <h1>${blogTitle}</h1>
        <div class="meta"><span class="cat">${selectedCategory}</span> &middot; ${new Date().toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})} &middot; ${readTime} min read &middot; ${wordCount} words</div>
        <div class="content">${blogContent.replace(/\n/g,'<br>')}</div>
        ${tags.length?`<div class="tags">Tags: ${tags.join(', ')}</div>`:''}
        <div class="footer">${companyName||'JobEng'} &middot; ${companyEmail||''}</div>
      </body></html>`;
      const {uri} = await Print.printToFileAsync({html,base64:false});
      if (await Sharing.isAvailableAsync()) await Sharing.shareAsync(uri,{mimeType:'application/pdf',dialogTitle:'Blog Post PDF'});
      else Alert.alert('Exported','PDF generated');
    } catch(e) { Alert.alert('Error',e.message); }
  };

  // ═══ Copy to clipboard ═══
  const handleCopyContent = async () => {
    try {
      await Clipboard.setStringAsync(blogTitle + '\n\n' + blogContent + (tags.length?'\n\nTags: '+tags.join(', '):''));
      Alert.alert('Copied','Blog content copied to clipboard');
    } catch { Alert.alert('Error','Could not copy'); }
  };

  // ═══ Auto-save ═══
  const handleAutoSave = async () => {
    if (!blogTitle.trim() || !blogContent.trim()) return;
    try {
      const bid = await AsyncStorage.getItem('company_id').catch(()=>'0');
      const key = `blog_autosave_${parseInt(bid)||0}`;
      await AsyncStorage.setItem(key, JSON.stringify({title:blogTitle,content:blogContent,category:selectedCategory,tags,featuredImage,metaTitle,metaDescription,focusKeyword,excerpt,slug,savedAt:new Date().toISOString()}));
      setLastSaved(new Date());
    } catch {}
  };

  // ═══ Load draft ═══
  const handleLoadDraft = (draft) => {
    Alert.alert('Load Draft',`Load "${draft.title}"? Current content will be replaced.`,[
      {text:'Cancel',style:'cancel'},
      {text:'Load',onPress:()=>{
        setBlogTitle(draft.title||'');
        setBlogContent(draft.content||draft.body||'');
        setSelectedCategory(draft.category||'Engineering');
        if(draft.tags) setTags(Array.isArray(draft.tags)?draft.tags:draft.tags.split(','));
        if(draft.featured_image) setFeaturedImage(draft.featured_image);
        setActiveTab('Edit');
      }},
    ]);
  };

  // ═══ Delete draft ═══
  const handleDeleteDraft = (id) => {
    Alert.alert('Delete','Remove this draft?',[
      {text:'Cancel',style:'cancel'},
      {text:'Delete',style:'destructive',onPress:async()=>{
        try { const h=await getH(); await fetch(`${API_BASE}/api/data/blog_posts/${id}`,{method:'DELETE',headers:h}).catch(()=>{}); } catch {}
        setDrafts(p=>p.filter(d=>d.id!==id));
      }},
    ]);
  };


  // ═══ AI Blog Writer ═══
  const AI_MODES = [
    {id:'write',label:'Write Full Article',icon:'file-document-edit',desc:'Generate a complete blog post from topic'},
    {id:'title',label:'Generate Titles',icon:'format-title',desc:'Suggest 5 engaging titles'},
    {id:'outline',label:'Create Outline',icon:'format-list-numbered',desc:'Generate structured outline'},
    {id:'improve',label:'Improve Content',icon:'auto-fix',desc:'Enhance existing text'},
    {id:'summarize',label:'Summarize',icon:'text-short',desc:'Create a concise summary'},
    {id:'seo',label:'SEO Optimize',icon:'magnify',desc:'Add keywords & meta tags'},
    {id:'intro',label:'Write Introduction',icon:'page-first',desc:'Generate opening paragraph'},
    {id:'conclusion',label:'Write Conclusion',icon:'page-last',desc:'Generate closing paragraph'},
    {id:'cta',label:'Call to Action',icon:'bullhorn',desc:'Generate compelling CTA'},
    {id:'social',label:'Social Captions',icon:'share-variant',desc:'Generate social media posts'},
  ];

  const AI_TONES = ['professional','casual','technical','persuasive','educational','friendly','authoritative','inspirational'];
  const AI_LENGTHS = [{id:'short',label:'Short (~300 words)'},{id:'medium',label:'Medium (~600 words)'},{id:'long',label:'Long (~1000 words)'},{id:'detailed',label:'Detailed (~1500 words)'}];

  const handleAIGenerate = async () => {
    const topic = aiPrompt.trim() || blogTitle.trim();
    if (!topic && aiMode !== 'improve' && aiMode !== 'summarize') {
      Alert.alert('Error', 'Enter a topic or blog title first');
      return;
    }
    setAiGenerating(true);
    setAiResult('');

    let systemPrompt = 'You are a professional blog writer for a UK engineering recruitment company called ' + (companyName || 'JobEng') + '. Write in British English.';
    let userPrompt = '';

    switch (aiMode) {
      case 'write':
        userPrompt = `Write a complete ${aiLength === 'short' ? '300' : aiLength === 'medium' ? '600' : aiLength === 'long' ? '1000' : '1500'} word blog post about: "${topic}".
Category: ${selectedCategory}. Tone: ${aiTone}.
Include: engaging introduction, 3-5 sections with subheadings, key insights, conclusion with call to action.
Do NOT include a title - just the body content.`;
        break;
      case 'title':
        userPrompt = `Generate 5 engaging blog titles for an article about: "${topic}". Category: ${selectedCategory}. Tone: ${aiTone}. Format as a numbered list.`;
        break;
      case 'outline':
        userPrompt = `Create a detailed outline for a blog post about: "${topic}". Category: ${selectedCategory}. Include: introduction, 4-6 main sections with bullet points for key content, conclusion. Tone: ${aiTone}.`;
        break;
      case 'improve':
        userPrompt = `Improve and enhance this blog content. Fix grammar, improve flow, add detail, make it more engaging. Tone: ${aiTone}.\n\nOriginal content:\n${blogContent || 'No content yet'}`;
        break;
      case 'summarize':
        userPrompt = `Summarize this blog post in 2-3 paragraphs:\n\n${blogContent || 'No content to summarize'}`;
        break;
      case 'seo':
        userPrompt = `Optimize this blog for SEO. Suggest: meta title (under 60 chars), meta description (under 160 chars), 5 focus keywords, and rewrite the first paragraph with keywords naturally included.\n\nTitle: ${blogTitle}\nContent: ${blogContent?.slice(0, 500) || topic}`;
        break;
      case 'intro':
        userPrompt = `Write an engaging introduction paragraph (100-150 words) for a blog post titled "${blogTitle || topic}". Category: ${selectedCategory}. Tone: ${aiTone}. Hook the reader immediately.`;
        break;
      case 'conclusion':
        userPrompt = `Write a strong conclusion paragraph (100-150 words) for a blog post titled "${blogTitle || topic}" about ${selectedCategory}. Include a call to action. Tone: ${aiTone}.`;
        break;
      case 'cta':
        userPrompt = `Generate 3 compelling call-to-action paragraphs for a blog post about "${blogTitle || topic}" for an engineering recruitment company. Include action verbs and urgency.`;
        break;
      case 'social':
        userPrompt = `Generate social media captions for sharing this blog post:\nTitle: "${blogTitle || topic}"\nCategory: ${selectedCategory}\n\nGenerate:\n1. LinkedIn post (professional, 150 words)\n2. Twitter/X post (280 chars max)\n3. Facebook post (casual, 100 words)\n4. Instagram caption with hashtags`;
        break;
    }

    try {
      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 2000,
          system: systemPrompt,
          messages: [{ role: 'user', content: userPrompt }],
        }),
      });

      const data = await response.json();
      const text = data?.content?.[0]?.text || data?.error?.message || 'No response generated. Check your connection.';
      setAiResult(text);
    } catch (e) {
      // Fallback: generate locally
      const fallbacks = {
        write: `## Introduction\n\n${topic} is a critical topic in the ${selectedCategory} sector. As the UK engineering industry continues to evolve, understanding this subject becomes increasingly important for professionals and businesses alike.\n\n## Key Insights\n\nThe engineering landscape in the UK is transforming rapidly. From sustainable construction practices to digital transformation, companies must adapt to remain competitive.\n\n## Industry Impact\n\nRecent data shows significant growth in ${selectedCategory.toLowerCase()}-related roles, with demand outstripping supply by considerable margins. This creates opportunities for both recruitment firms and engineering professionals.\n\n## Best Practices\n\nTo succeed in this environment, organisations should focus on:\n- Investing in modern technology and training\n- Building strong talent pipelines\n- Embracing sustainable engineering practices\n- Fostering collaborative work environments\n\n## Conclusion\n\nAs the ${selectedCategory.toLowerCase()} sector continues to grow, staying informed and adaptable is essential. Contact ${companyName || 'us'} to learn how we can help you navigate these changes and find the right engineering talent for your projects.`,
        title: `1. The Complete Guide to ${topic}: What Every UK Engineer Should Know\n2. ${topic}: Transforming the Future of British Engineering\n3. Why ${topic} Matters for Construction Professionals in 2026\n4. Unlocking Growth Through ${topic}: An Industry Perspective\n5. ${topic} Best Practices: Lessons from Leading UK Engineering Firms`,
        outline: `## Outline: ${topic}\n\n### I. Introduction\n- Hook: Current state of ${selectedCategory.toLowerCase()}\n- Thesis statement\n- What readers will learn\n\n### II. Background & Context\n- Industry overview\n- Recent developments\n- Key statistics\n\n### III. Main Analysis\n- Core principles\n- Case studies\n- Expert insights\n\n### IV. Practical Applications\n- Implementation steps\n- Best practices\n- Common pitfalls\n\n### V. Future Outlook\n- Industry trends\n- Predictions\n- Opportunities\n\n### VI. Conclusion\n- Key takeaways\n- Call to action`,
        improve: (blogContent || 'No content to improve') + '\n\n[AI Enhancement: Consider adding more specific UK industry data, expert quotes, and actionable takeaways to strengthen this piece.]',
        summarize: `This article explores ${blogTitle || topic}, covering key aspects of ${selectedCategory.toLowerCase()} in the UK engineering sector. The main points include industry trends, best practices, and practical recommendations for professionals.`,
        seo: `Meta Title: ${blogTitle || topic} | ${companyName || 'JobEng'} Engineering Insights\nMeta Description: Discover essential insights about ${topic} in the UK engineering sector. Expert analysis, best practices, and actionable advice for professionals.\n\nFocus Keywords: ${topic.toLowerCase()}, uk engineering, ${selectedCategory.toLowerCase()}, engineering recruitment, construction industry\n\nSEO-Optimized Introduction:\n${topic} is reshaping the UK engineering landscape. As industry leaders seek innovative solutions, understanding the latest developments in ${selectedCategory.toLowerCase()} has never been more critical for engineering professionals and construction firms across Britain.`,
        intro: `In an era where ${selectedCategory.toLowerCase()} is evolving at unprecedented speed, ${topic} has emerged as a pivotal factor shaping the UK engineering sector. Whether you are a seasoned professional or an emerging talent, understanding these dynamics is essential for staying ahead of the curve. In this article, we explore the key aspects that every engineering professional should know.`,
        conclusion: `As we have explored, ${topic} represents a significant opportunity for the UK engineering sector. By embracing these insights and taking proactive steps, professionals and organisations can position themselves for success in an increasingly competitive landscape. Ready to take the next step? Contact ${companyName || 'JobEng'} today to discover how we can support your engineering career or help you find exceptional talent for your next project.`,
        cta: `1. Ready to advance your engineering career? Browse our latest ${selectedCategory.toLowerCase()} opportunities and take the next step today. Visit ${companyName || 'JobEng'} to get started.\n\n2. Looking for top engineering talent? Our specialist recruiters have placed thousands of qualified professionals across the UK. Get in touch now for a free consultation.\n\n3. Stay ahead of industry trends. Subscribe to our newsletter for weekly insights on ${selectedCategory.toLowerCase()}, career advice, and exclusive job opportunities delivered straight to your inbox.`,
        social: `LinkedIn: Excited to share our latest insights on ${topic}! As the UK engineering sector evolves, staying informed is key. Read our full analysis and discover what it means for your career or business.\n\nTwitter/X: New blog: ${blogTitle || topic} - essential reading for UK engineering professionals. Key takeaways inside.\n\nFacebook: We just published a deep dive into ${topic}! Whether you are hiring or job seeking in engineering, this is a must-read.\n\nInstagram: Exploring ${topic} in UK engineering. Swipe for key insights! #engineering #ukjobs #construction #recruitment #${selectedCategory.toLowerCase().replace(/ /g, '')}`,
      };
      setAiResult(fallbacks[aiMode] || 'AI generation is available when connected to the internet. Try again later.');
    }
    setAiGenerating(false);
  };

  const handleInsertAI = (mode) => {
    if (!aiResult) return;
    switch (mode) {
      case 'replace':
        setBlogContent(aiResult);
        break;
      case 'append':
        setBlogContent(prev => prev + (prev ? '\n\n' : '') + aiResult);
        break;
      case 'prepend':
        setBlogContent(prev => aiResult + (prev ? '\n\n' : '') + prev);
        break;
      case 'title':
        const firstLine = aiResult.split('\n')[0].replace(/^\d+\.\s*/, '').trim();
        setBlogTitle(firstLine);
        break;
      case 'meta':
        const metaMatch = aiResult.match(/Meta Title:\s*(.+)/i);
        const descMatch = aiResult.match(/Meta Description:\s*(.+)/i);
        if (metaMatch) setMetaTitle(metaMatch[1].trim());
        if (descMatch) setMetaDescription(descMatch[1].trim());
        break;
      case 'excerpt':
        setExcerpt(aiResult.slice(0, 300));
        break;
    }
    setShowAIModal(false);
    Alert.alert('Done', 'AI content inserted into your blog');
  };

  // ═══ Share to social ═══
  const handleShareSocial = async (platform) => {
    const text = blogTitle + '\n\n' + (excerpt||blogContent.slice(0,200)) + '...\n\n' + (tags.length?tags.map(t=>'#'+t.replace(/\s/g,'')).join(' '):'');
    if (await Sharing.isAvailableAsync()) {
      await Sharing.shareAsync('data:text/plain,' + encodeURIComponent(text), {dialogTitle:`Share to ${platform}`});
    } else {
      try { await Clipboard.setStringAsync(text); Alert.alert('Copied','Share text copied to clipboard — paste in '+platform); } catch {}
    }
  };

  // Campaign context banner
  const renderCampaignBanner = () => campaign ? (
    <View style={{ flexDirection:'row', alignItems:'center', backgroundColor:'#eff6ff', padding:10, margin:10, borderRadius:10, borderWidth:1, borderColor:'#bfdbfe' }}>
      <MaterialCommunityIcons name="bullhorn-outline" size={16} color="#0e4e96" />
      <Text style={{ flex:1, marginLeft:8, fontSize:12, color:'#1e40af', fontWeight:'600' }}>
        Creating content for: {campaign.name || campaign.campaign_name}
      </Text>
      <MaterialCommunityIcons name="link" size={14} color="#0e4e96" />
    </View>
  ) : null;

  // Render Edit Tab
  const renderEditTab = () => (
    <View style={styles.tabContent}>
      <View style={styles.inputContainer}>
        <Text style={styles.inputLabel}>Blog Title</Text>
        <TextInput
          style={styles.titleInput}
          value={blogTitle}
          onChangeText={setBlogTitle}
          placeholder="Enter blog title"
          placeholderTextColor="#999"
        />
      </View>

      <View style={styles.inputContainer}>
        <Text style={styles.inputLabel}>Category</Text>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.categoryScroll}
        >
          {categoryOptions.map((category, index) => (
            <TouchableOpacity
              key={`category-${index}`}
              style={[
                styles.categoryButton,
                selectedCategory === category && styles.selectedCategoryButton,
              ]}
              onPress={() => setSelectedCategory(category)}
            >
              <Text
                style={[
                  styles.categoryButtonText,
                  selectedCategory === category &&
                    styles.selectedCategoryButtonText,
                ]}
              >
                {category}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      <View style={styles.inputContainer}>
        <Text style={styles.inputLabel}>Featured Image</Text>
        {featuredImage ? (
          <View style={styles.featuredImageContainer}>
            <View style={styles.featuredImagePreview}>
              <MaterialCommunityIcons name="image" size={40} color="#0e4e96" />
              <Text style={styles.featuredImageText}>Image Selected</Text>
            </View>
            <TouchableOpacity
              style={styles.changeImageButton}
              onPress={handleSelectFeaturedImage}
            >
              <Text style={styles.changeImageButtonText}>Change</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <TouchableOpacity
            style={styles.selectImageButton}
            onPress={handleSelectFeaturedImage}
          >
            <MaterialCommunityIcons
              name="image-plus"
              size={24}
              color="#0e4e96"
            />
            <Text style={styles.selectImageButtonText}>
              Select Featured Image
            </Text>
          </TouchableOpacity>
        )}
      </View>

      <View style={styles.formattingToolbar}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          {formatOptions.map((option, index) => (
            <FormatOption
              key={`format-${index}`}
              icon={option.icon}
              label={option.label}
              selected={selectedFormat === option.label}
              onPress={() => handleFormat(option.label)}
            />
          ))}
        </ScrollView>
        <View style={styles.colorPickerWrapper}>
          <ColorPicker
            colors={colorOptions}
            selectedColor={selectedColor}
            onSelectColor={setSelectedColor}
          />
        </View>
      </View>

      {/* AI Writing Assistant Button */}
      <TouchableOpacity style={{backgroundColor:'#8B5CF6',borderRadius:12,paddingVertical:12,alignItems:'center',flexDirection:'row',justifyContent:'center',gap:8,marginBottom:12}}
        onPress={() => setShowAIModal(true)}>
        <MaterialCommunityIcons name="robot" size={18} color="#fff"/>
        <Text style={{fontSize:14,fontWeight:'700',color:'#fff'}}>AI Writing Assistant</Text>
      </TouchableOpacity>

      <View style={styles.inputContainer}>
        <Text style={styles.inputLabel}>Blog Content</Text>
        <TextInput
          style={styles.contentInput}
          value={blogContent}
          onChangeText={setBlogContent}
          placeholder="Write your blog content here..."
          placeholderTextColor="#999"
          multiline
          textAlignVertical="top"
        />
      </View>

      {/* Word Count Bar */}
      <View style={{flexDirection:'row',justifyContent:'space-between',paddingHorizontal:4,marginBottom:12}}>
        <Text style={{fontSize:10,color:'#94A3B8'}}>{wordCount} words · {charCount} chars · {readTime} min read</Text>
        {lastSaved && <Text style={{fontSize:10,color:'#10B981'}}>Saved {lastSaved.toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'})}</Text>}
      </View>

      {/* Excerpt */}
      <View style={styles.inputContainer}>
        <Text style={styles.inputLabel}>Excerpt / Summary</Text>
        <TextInput
          style={[styles.contentInput, {minHeight:60}]}
          value={excerpt}
          onChangeText={setExcerpt}
          placeholder="Brief summary for search results..."
          placeholderTextColor="#999"
          multiline
          textAlignVertical="top"
        />
      </View>

      <View style={styles.inputContainer}>
        <Text style={styles.inputLabel}>Tags</Text>
        <View style={styles.tagsInputContainer}>
          <TextInput
            style={styles.tagInput}
            value={currentTag}
            onChangeText={setCurrentTag}
            placeholder="Add a tag"
            placeholderTextColor="#999"
            onSubmitEditing={handleAddTag}
          />
          <TouchableOpacity style={styles.addTagButton} onPress={handleAddTag}>
            <Ionicons name="add" size={24} color="#fff" />
          </TouchableOpacity>
        </View>

        <View style={styles.tagsContainer}>
          {tags.map((tag, index) => (
            <View key={`tag-${index}`} style={styles.tag}>
              <Text style={styles.tagText}>{tag}</Text>
              <TouchableOpacity
                style={styles.removeTagButton}
                onPress={() => handleRemoveTag(tag)}
              >
                <Ionicons name="close" size={16} color="#666" />
              </TouchableOpacity>
            </View>
          ))}
        </View>
      </View>
    </View>
  );

  // Render Preview Tab
  const renderPreviewTab = () => (
    <View style={styles.tabContent}>
      {!blogTitle || !blogContent ? (
        <View style={styles.emptyPreviewContainer}>
          <MaterialCommunityIcons
            name="file-document-edit-outline"
            size={60}
            color="#ccc"
          />
          <Text style={styles.emptyPreviewText}>
            Add title and content to preview your blog
          </Text>
          <TouchableOpacity
            style={styles.emptyPreviewButton}
            onPress={() => setActiveTab('Edit')}
          >
            <Text style={styles.emptyPreviewButtonText}>Go to Editor</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <View style={styles.previewContainer}>
          {featuredImage && (
            featuredImage.startsWith('http') || featuredImage.startsWith('file') ? (
              <Image source={{uri:featuredImage}} style={{width:'100%',height:200,borderRadius:12,marginBottom:16}} resizeMode="cover" onError={()=>{}}/>
            ) : (
              <View style={styles.previewImagePlaceholder}>
                <MaterialCommunityIcons name="image" size={40} color="#0e4e96" />
                <Text style={styles.previewImageText}>Featured Image</Text>
              </View>
            )
          )}

          <Text style={styles.previewTitle}>{blogTitle}</Text>

          <View style={styles.previewMeta}>
            <View style={styles.previewCategoryBadge}>
              <Text style={styles.previewCategoryText}>{selectedCategory}</Text>
            </View>
            <Text style={styles.previewDate}>
              {new Date().toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })} · {readTime} min read · {wordCount} words
            </Text>
          </View>

          <Text style={styles.previewContent}>{blogContent}</Text>

          {tags.length > 0 && (
            <View style={styles.previewTagsContainer}>
              <Text style={styles.previewTagsLabel}>Tags:</Text>
              <View style={styles.previewTags}>
                {tags.map((tag, index) => (
                  <View key={`preview-tag-${index}`} style={styles.previewTag}>
                    <Text style={styles.previewTagText}>{tag}</Text>
                  </View>
                ))}
              </View>
            </View>
          )}
        </View>
      )}
    </View>
  );

  // Render Settings Tab
  const renderSettingsTab = () => (
    <View style={styles.tabContent}>
      <View style={styles.settingsSection}>
        <Text style={styles.settingsSectionTitle}>Publishing Options</Text>

        <View style={styles.settingItem}>
          <View style={styles.settingInfo}>
            <Text style={styles.settingLabel}>Status</Text>
            <Text style={styles.settingValue}>
              <Text>{isPublished ? 'Published' : 'Draft'}</Text>
            </Text>
          </View>
          <View
            style={[
              styles.statusBadge,
              isPublished ? styles.publishedBadge : styles.draftBadge,
            ]}
          >
            <Text style={styles.statusBadgeText}>
              <Text>{isPublished ? 'Published' : 'Draft'}</Text>
            </Text>
          </View>
        </View>

        <View style={styles.settingItem}>
          <View style={styles.settingInfo}>
            <Text style={styles.settingLabel}>Visibility</Text>
            <Text style={styles.settingValue}>{visibility}</Text>
          </View>
          <TouchableOpacity onPress={() => {
            Alert.alert('Visibility','',[
              {text:'Public',onPress:()=>setVisibility('public')},
              {text:'Private',onPress:()=>setVisibility('private')},
              {text:'Password Protected',onPress:()=>setVisibility('protected')},
              {text:'Cancel',style:'cancel'},
            ]);
          }}>
            <Text style={styles.settingActionText}>Change</Text>
          </TouchableOpacity>
        </View>

        <View style={styles.settingItem}>
          <View style={styles.settingInfo}>
            <Text style={styles.settingLabel}>Schedule</Text>
            <Text style={styles.settingValue}>{scheduleDate||'Publish immediately'}</Text>
          </View>
          <TouchableOpacity onPress={() => {
            Alert.alert('Schedule','',[
              {text:'Now',onPress:()=>setScheduleDate('')},
              {text:'Tomorrow 9AM',onPress:()=>{const d=new Date();d.setDate(d.getDate()+1);d.setHours(9,0,0,0);setScheduleDate(d.toLocaleDateString('en-GB',{day:'numeric',month:'short'})+' 9:00 AM');}},
              {text:'Next Monday',onPress:()=>{const d=new Date();d.setDate(d.getDate()+(8-d.getDay())%7||7);d.setHours(10,0,0,0);setScheduleDate(d.toLocaleDateString('en-GB',{day:'numeric',month:'short'})+' 10:00 AM');}},
              {text:'Cancel',style:'cancel'},
            ]);
          }}>
            <Text style={styles.settingActionText}>Edit</Text>
          </TouchableOpacity>
        </View>
      </View>

      <View style={styles.settingsSection}>
        <Text style={styles.settingsSectionTitle}>SEO Settings</Text>

        <View style={styles.inputContainer}>
          <Text style={styles.inputLabel}>Meta Title</Text>
          <TextInput
            style={styles.settingInput}
            placeholder="Enter meta title"
            placeholderTextColor="#999"
            value={metaTitle||blogTitle}
            onChangeText={setMetaTitle}
          />
        </View>

        <View style={styles.inputContainer}>
          <Text style={styles.inputLabel}>Meta Description</Text>
          <TextInput
            style={[styles.settingInput, styles.textArea]}
            placeholder="Enter meta description"
            placeholderTextColor="#999"
            value={metaDescription}
            onChangeText={setMetaDescription}
            multiline
            numberOfLines={3}
            textAlignVertical="top"
          />
        </View>

        <View style={styles.inputContainer}>
          <Text style={styles.inputLabel}>Focus Keyword</Text>
          <TextInput
            style={styles.settingInput}
            placeholder="Enter focus keyword"
            placeholderTextColor="#999"
            value={focusKeyword}
            onChangeText={setFocusKeyword}
          />
        </View>
      </View>

      <View style={styles.settingsSection}>
        <Text style={styles.settingsSectionTitle}>Social Sharing</Text>

        <TouchableOpacity style={styles.socialSharingButton} onPress={()=>handleShareSocial('LinkedIn')}>
          <MaterialCommunityIcons name="linkedin" size={24} color="#fff" />
          <Text style={styles.socialSharingButtonText}>Share to LinkedIn</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.socialSharingButton, styles.twitterButton]}
          onPress={()=>handleShareSocial('Twitter')}
        >
          <MaterialCommunityIcons name="twitter" size={24} color="#fff" />
          <Text style={styles.socialSharingButtonText}>Share to Twitter</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.socialSharingButton, styles.facebookButton]}
          onPress={()=>handleShareSocial('Facebook')}
        >
          <MaterialCommunityIcons name="facebook" size={24} color="#fff" />
          <Text style={styles.socialSharingButtonText}>Share to Facebook</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  // Render active tab content
  // ═══ DRAFTS TAB ═══
  const renderDraftsTab = () => (
    <View style={styles.tabContent}>
      <Text style={styles.settingsSectionTitle}>Your Drafts ({drafts.length})</Text>
      {drafts.length === 0 ? (
        <View style={{alignItems:'center',paddingVertical:40}}>
          <MaterialCommunityIcons name="file-document-outline" size={48} color="#CBD5E1"/>
          <Text style={{color:'#94A3B8',marginTop:12}}>No drafts yet</Text>
          <TouchableOpacity style={{backgroundColor:'#0e4e96',paddingHorizontal:20,paddingVertical:10,borderRadius:10,marginTop:12}}
            onPress={()=>setActiveTab('Edit')}>
            <Text style={{color:'#fff',fontWeight:'700'}}>Start Writing</Text>
          </TouchableOpacity>
        </View>
      ) : drafts.map((d,i) => (
        <View key={d.id||i} style={{backgroundColor:'#fff',borderRadius:12,padding:14,marginBottom:10,borderWidth:1,borderColor:'#E2E8F0'}}>
          <View style={{flexDirection:'row',justifyContent:'space-between',marginBottom:4}}>
            <Text style={{fontSize:14,fontWeight:'700',color:'#1E293B',flex:1}} numberOfLines={1}>{d.title||'Untitled'}</Text>
            <View style={{backgroundColor:d.status==='published'?'#D1FAE5':'#FEF3C7',paddingHorizontal:8,paddingVertical:2,borderRadius:6}}>
              <Text style={{fontSize:9,fontWeight:'700',color:d.status==='published'?'#065F46':'#92400E'}}>{d.status||'draft'}</Text>
            </View>
          </View>
          <Text style={{fontSize:11,color:'#64748B'}} numberOfLines={2}>{d.content||d.body||'No content'}</Text>
          <View style={{flexDirection:'row',justifyContent:'space-between',marginTop:8,paddingTop:8,borderTopWidth:1,borderTopColor:'#F1F5F9'}}>
            <Text style={{fontSize:10,color:'#94A3B8'}}>{d.category||'General'} · {d.created_at?new Date(d.created_at).toLocaleDateString('en-GB',{day:'numeric',month:'short'}):''}</Text>
            <View style={{flexDirection:'row',gap:12}}>
              <TouchableOpacity onPress={()=>handleLoadDraft(d)}>
                <Text style={{fontSize:11,fontWeight:'600',color:'#0e4e96'}}>Load</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={()=>handleDeleteDraft(d.id)}>
                <Text style={{fontSize:11,fontWeight:'600',color:'#EF4444'}}>Delete</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      ))}
    </View>
  );

  // ═══ SEO TAB ═══
  const renderSEOTab = () => {
    const titleScore = metaTitle.length > 10 && metaTitle.length < 60 ? 'Good' : metaTitle.length === 0 ? 'Missing' : 'Needs Work';
    const descScore = metaDescription.length > 50 && metaDescription.length < 160 ? 'Good' : metaDescription.length === 0 ? 'Missing' : 'Needs Work';
    const kwScore = focusKeyword && blogContent.toLowerCase().includes(focusKeyword.toLowerCase()) ? 'Found' : focusKeyword ? 'Not in content' : 'Missing';
    const slugScore = slug.length > 3 ? 'Good' : 'Missing';
    const imgScore = featuredImage ? 'Good' : 'Missing';
    const scores = [titleScore,descScore,kwScore,slugScore,imgScore].filter(s=>s==='Good').length;
    const seoScore = Math.round(scores / 5 * 100);
    return (
      <View style={styles.tabContent}>
        {/* SEO Score */}
        <View style={{backgroundColor:'#fff',borderRadius:14,padding:16,marginBottom:16,borderWidth:1,borderColor:'#E2E8F0',alignItems:'center'}}>
          <Text style={{fontSize:12,color:'#64748B',marginBottom:6}}>SEO Score</Text>
          <View style={{width:70,height:70,borderRadius:35,borderWidth:5,borderColor:seoScore>=80?'#10B981':seoScore>=50?'#F59E0B':'#EF4444',alignItems:'center',justifyContent:'center'}}>
            <Text style={{fontSize:24,fontWeight:'900',color:seoScore>=80?'#10B981':seoScore>=50?'#F59E0B':'#EF4444'}}>{seoScore}</Text>
          </View>
          <Text style={{fontSize:11,color:seoScore>=80?'#10B981':seoScore>=50?'#F59E0B':'#EF4444',fontWeight:'600',marginTop:4}}>{seoScore>=80?'Great':'Needs improvement'}</Text>
        </View>

        {/* SEO Checklist */}
        <View style={{backgroundColor:'#fff',borderRadius:14,padding:14,marginBottom:16,borderWidth:1,borderColor:'#E2E8F0'}}>
          {[{l:'Meta Title',v:titleScore,d:`${metaTitle.length}/60 chars`},{l:'Meta Description',v:descScore,d:`${metaDescription.length}/160 chars`},{l:'Focus Keyword',v:kwScore,d:focusKeyword||'Not set'},{l:'URL Slug',v:slugScore,d:slug||'Auto-generated'},{l:'Featured Image',v:imgScore,d:featuredImage?'Set':'Not set'}].map((item,i) => (
            <View key={i} style={{flexDirection:'row',alignItems:'center',paddingVertical:10,borderBottomWidth:i<4?1:0,borderBottomColor:'#F1F5F9'}}>
              <MaterialCommunityIcons name={item.v==='Good'||item.v==='Found'?'check-circle':'alert-circle'} size={18} color={item.v==='Good'||item.v==='Found'?'#10B981':item.v==='Missing'?'#EF4444':'#F59E0B'}/>
              <View style={{flex:1,marginLeft:10}}>
                <Text style={{fontSize:13,fontWeight:'600',color:'#334155'}}>{item.l}</Text>
                <Text style={{fontSize:10,color:'#94A3B8'}}>{item.d}</Text>
              </View>
              <Text style={{fontSize:10,fontWeight:'700',color:item.v==='Good'||item.v==='Found'?'#10B981':item.v==='Missing'?'#EF4444':'#F59E0B'}}>{item.v}</Text>
            </View>
          ))}
        </View>

        {/* SEO Fields */}
        <View style={styles.inputContainer}>
          <Text style={styles.inputLabel}>Meta Title ({metaTitle.length}/60)</Text>
          <TextInput style={styles.settingInput} value={metaTitle} onChangeText={setMetaTitle} placeholder={blogTitle||'Enter meta title'} placeholderTextColor="#999"/>
        </View>
        <View style={styles.inputContainer}>
          <Text style={styles.inputLabel}>Meta Description ({metaDescription.length}/160)</Text>
          <TextInput style={[styles.settingInput,{minHeight:60}]} value={metaDescription} onChangeText={setMetaDescription} placeholder="Describe your post for search engines..." placeholderTextColor="#999" multiline textAlignVertical="top"/>
        </View>
        <View style={styles.inputContainer}>
          <Text style={styles.inputLabel}>Focus Keyword</Text>
          <TextInput style={styles.settingInput} value={focusKeyword} onChangeText={setFocusKeyword} placeholder="Main keyword for this post" placeholderTextColor="#999"/>
        </View>
        <View style={styles.inputContainer}>
          <Text style={styles.inputLabel}>URL Slug</Text>
          <TextInput style={styles.settingInput} value={slug} onChangeText={setSlug} placeholder="url-friendly-slug" placeholderTextColor="#999"/>
        </View>
      </View>
    );
  };

  // ═══ EXPORT TAB ═══
  const renderExportTab = () => (
    <View style={styles.tabContent}>
      <Text style={styles.settingsSectionTitle}>Export & Share</Text>

      {/* Stats */}
      <View style={{flexDirection:'row',gap:8,marginBottom:16}}>
        {[{l:'Words',v:String(wordCount),c:'#0e4e96',i:'file-word'},{l:'Chars',v:String(charCount),c:'#8B5CF6',i:'format-letter-case'},{l:'Read Time',v:readTime+'min',c:'#10B981',i:'clock'},{l:'Tags',v:String(tags.length),c:'#F59E0B',i:'tag'}].map((s,i)=>(
          <View key={i} style={{flex:1,backgroundColor:'#fff',borderRadius:10,padding:10,alignItems:'center',borderWidth:1,borderColor:'#E2E8F0'}}>
            <MaterialCommunityIcons name={s.i} size={16} color={s.c}/>
            <Text style={{fontSize:14,fontWeight:'900',color:s.c,marginTop:2}}>{s.v}</Text>
            <Text style={{fontSize:8,color:'#94A3B8'}}>{s.l}</Text>
          </View>
        ))}
      </View>

      {/* Export Actions */}
      <TouchableOpacity style={{backgroundColor:'#EF4444',paddingVertical:14,borderRadius:12,alignItems:'center',flexDirection:'row',justifyContent:'center',gap:8,marginBottom:10}}
        onPress={handleExportPDF}>
        <MaterialCommunityIcons name="file-pdf-box" size={20} color="#fff"/>
        <Text style={{fontSize:14,fontWeight:'700',color:'#fff'}}>Export as PDF</Text>
      </TouchableOpacity>

      <TouchableOpacity style={{backgroundColor:'#0e4e96',paddingVertical:14,borderRadius:12,alignItems:'center',flexDirection:'row',justifyContent:'center',gap:8,marginBottom:10}}
        onPress={handleCopyContent}>
        <Ionicons name="copy" size={18} color="#fff"/>
        <Text style={{fontSize:14,fontWeight:'700',color:'#fff'}}>Copy to Clipboard</Text>
      </TouchableOpacity>

      <View style={{flexDirection:'row',gap:8,marginBottom:16}}>
        <TouchableOpacity style={{flex:1,backgroundColor:'#0A66C2',paddingVertical:12,borderRadius:10,alignItems:'center',flexDirection:'row',justifyContent:'center',gap:6}}
          onPress={()=>handleShareSocial('LinkedIn')}>
          <FontAwesome5 name="linkedin" size={16} color="#fff"/><Text style={{fontSize:12,fontWeight:'700',color:'#fff'}}>LinkedIn</Text>
        </TouchableOpacity>
        <TouchableOpacity style={{flex:1,backgroundColor:'#1DA1F2',paddingVertical:12,borderRadius:10,alignItems:'center',flexDirection:'row',justifyContent:'center',gap:6}}
          onPress={()=>handleShareSocial('Twitter')}>
          <FontAwesome5 name="twitter" size={16} color="#fff"/><Text style={{fontSize:12,fontWeight:'700',color:'#fff'}}>Twitter</Text>
        </TouchableOpacity>
        <TouchableOpacity style={{flex:1,backgroundColor:'#1877F2',paddingVertical:12,borderRadius:10,alignItems:'center',flexDirection:'row',justifyContent:'center',gap:6}}
          onPress={()=>handleShareSocial('Facebook')}>
          <FontAwesome5 name="facebook" size={16} color="#fff"/><Text style={{fontSize:12,fontWeight:'700',color:'#fff'}}>Facebook</Text>
        </TouchableOpacity>
      </View>

      <TouchableOpacity style={{backgroundColor:'#10B981',paddingVertical:14,borderRadius:12,alignItems:'center',flexDirection:'row',justifyContent:'center',gap:8}}
        onPress={handleAutoSave}>
        <Ionicons name="save" size={18} color="#fff"/>
        <Text style={{fontSize:14,fontWeight:'700',color:'#fff'}}>Save Auto-Draft</Text>
      </TouchableOpacity>
    </View>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'Edit':
        return renderEditTab();
      case 'Preview':
        return renderPreviewTab();
      case 'Drafts':
        return renderDraftsTab();
      case 'SEO':
        return renderSEOTab();
      case 'Settings':
        return renderSettingsTab();
      case 'Export':
        return renderExportTab();
      default:
        return renderEditTab();
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <Ionicons name="chevron-back" size={24} color="#fff" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Blog Editor</Text>
        <CompanyLogo uri={companyLogo} size={30} />
      </View>

      {/* Tab Navigation */}
      <View style={styles.tabContainer}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{paddingHorizontal:4}}>
        {['Edit', 'Preview', 'Drafts', 'SEO', 'Settings', 'Export'].map(tab => (
          <TouchableOpacity
            key={`tab-${tab}`}
            style={[
              styles.tabButton,
              activeTab === tab && styles.activeTabButton,
            ]}
            onPress={() => setActiveTab(tab)}
          >
            <Text
              style={[
                styles.tabText,
                activeTab === tab && styles.activeTabText,
              ]}
            >
              {tab}
            </Text>
          </TouchableOpacity>
        ))}
        </ScrollView>
      </View>

      {/* Tab Content */}
      <ScrollView style={styles.contentContainer}>
        {renderTabContent()}
      </ScrollView>

      {/* Action Buttons */}
      <View style={styles.actionButtonsContainer}>
        <TouchableOpacity
          style={styles.saveButton}
          onPress={() => handleSaveBlog(false)}
          disabled={isSaving}
        >
          {isSaving && !isPublished ? (
            <ActivityIndicator size={20} color="#fff" />
          ) : (
            <>
              <MaterialCommunityIcons
                name="content-save-outline"
                size={20}
                color="#fff"
              />
              <Text style={styles.saveButtonText}>Save Draft</Text>
            </>
          )}
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.publishButton}
          onPress={() => handleSaveBlog(true)}
          disabled={isSaving}
        >
          {isSaving && isPublished ? (
            <ActivityIndicator size={20} color="#fff" />
          ) : (
            <>
              <MaterialCommunityIcons name="publish" size={20} color="#fff" />
              <Text style={styles.publishButtonText}>Publish</Text>
            </>
          )}
        </TouchableOpacity>
      </View>

      {/* AI Writing Modal */}
      <Modal visible={showAIModal} animationType="slide" presentationStyle="pageSheet">
        <SafeAreaView style={{flex:1,backgroundColor:'#F8FAFC'}}>
          <View style={{flexDirection:'row',alignItems:'center',padding:14,backgroundColor:'#fff',borderBottomWidth:1,borderBottomColor:'#E2E8F0'}}>
            <TouchableOpacity onPress={() => setShowAIModal(false)}><Ionicons name="close" size={24} color="#555"/></TouchableOpacity>
            <View style={{flex:1,alignItems:'center'}}>
              <Text style={{fontSize:16,fontWeight:'800',color:'#8B5CF6'}}>AI Writing Assistant</Text>
              <Text style={{fontSize:10,color:'#94A3B8'}}>Powered by Claude AI</Text>
            </View>
            <MaterialCommunityIcons name="robot" size={24} color="#8B5CF6"/>
          </View>
          <ScrollView style={{flex:1,padding:14}}>
            {/* AI Mode Selection */}
            <Text style={{fontSize:13,fontWeight:'700',color:'#334155',marginBottom:8}}>What should AI do?</Text>
            <View style={{flexDirection:'row',flexWrap:'wrap',gap:8,marginBottom:16}}>
              {AI_MODES.map(m => (
                <TouchableOpacity key={m.id} style={{width:'48%',flexDirection:'row',alignItems:'center',gap:8,backgroundColor:aiMode===m.id?'#8B5CF615':'#fff',borderRadius:10,padding:10,borderWidth:1,borderColor:aiMode===m.id?'#8B5CF6':'#E2E8F0'}}
                  onPress={() => setAiMode(m.id)}>
                  <MaterialCommunityIcons name={m.icon} size={18} color={aiMode===m.id?'#8B5CF6':'#94A3B8'}/>
                  <View style={{flex:1}}>
                    <Text style={{fontSize:11,fontWeight:'700',color:aiMode===m.id?'#8B5CF6':'#334155'}}>{m.label}</Text>
                    <Text style={{fontSize:8,color:'#94A3B8'}}>{m.desc}</Text>
                  </View>
                </TouchableOpacity>
              ))}
            </View>

            {/* Topic/Prompt */}
            <Text style={{fontSize:13,fontWeight:'700',color:'#334155',marginBottom:6}}>Topic / Instructions</Text>
            <TextInput style={{backgroundColor:'#fff',borderRadius:10,padding:12,borderWidth:1,borderColor:'#E2E8F0',fontSize:13,minHeight:60,marginBottom:12}}
              value={aiPrompt} onChangeText={setAiPrompt}
              placeholder={blogTitle || "Enter your blog topic or instructions..."}
              multiline textAlignVertical="top"/>

            {/* Tone */}
            <Text style={{fontSize:13,fontWeight:'700',color:'#334155',marginBottom:6}}>Tone</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{marginBottom:12}}>
              <View style={{flexDirection:'row',gap:6}}>
                {AI_TONES.map(t => (
                  <TouchableOpacity key={t} style={{paddingHorizontal:14,paddingVertical:6,borderRadius:8,backgroundColor:aiTone===t?'#8B5CF6':'#F1F5F9'}}
                    onPress={() => setAiTone(t)}>
                    <Text style={{fontSize:11,fontWeight:'600',color:aiTone===t?'#fff':'#64748B'}}>{t}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </ScrollView>

            {/* Length (for write mode) */}
            {aiMode === 'write' && (<>
              <Text style={{fontSize:13,fontWeight:'700',color:'#334155',marginBottom:6}}>Length</Text>
              <View style={{flexDirection:'row',gap:6,marginBottom:12}}>
                {AI_LENGTHS.map(l => (
                  <TouchableOpacity key={l.id} style={{flex:1,paddingVertical:8,borderRadius:8,alignItems:'center',backgroundColor:aiLength===l.id?'#8B5CF6':'#F1F5F9'}}
                    onPress={() => setAiLength(l.id)}>
                    <Text style={{fontSize:10,fontWeight:'600',color:aiLength===l.id?'#fff':'#64748B'}}>{l.label}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </>)}

            {/* Generate Button */}
            <TouchableOpacity style={{backgroundColor:'#8B5CF6',paddingVertical:14,borderRadius:12,alignItems:'center',flexDirection:'row',justifyContent:'center',gap:8,marginBottom:16}}
              onPress={handleAIGenerate} disabled={aiGenerating}>
              {aiGenerating ? (
                <><ActivityIndicator color="#fff" size="small"/><Text style={{fontSize:14,fontWeight:'700',color:'#fff'}}>Generating...</Text></>
              ) : (
                <><MaterialCommunityIcons name="creation" size={20} color="#fff"/><Text style={{fontSize:14,fontWeight:'700',color:'#fff'}}>Generate with AI</Text></>
              )}
            </TouchableOpacity>

            {/* AI Result */}
            {aiResult ? (
              <View style={{backgroundColor:'#fff',borderRadius:14,padding:16,borderWidth:1,borderColor:'#8B5CF630'}}>
                <View style={{flexDirection:'row',alignItems:'center',gap:6,marginBottom:10}}>
                  <MaterialCommunityIcons name="sparkles" size={16} color="#8B5CF6"/>
                  <Text style={{fontSize:14,fontWeight:'800',color:'#8B5CF6'}}>AI Generated Content</Text>
                </View>
                <Text style={{fontSize:13,color:'#334155',lineHeight:20,marginBottom:16}} selectable>{aiResult}</Text>

                {/* Insert Options */}
                <Text style={{fontSize:11,fontWeight:'700',color:'#64748B',marginBottom:8}}>Insert into blog:</Text>
                <View style={{flexDirection:'row',flexWrap:'wrap',gap:8}}>
                  <TouchableOpacity style={{backgroundColor:'#8B5CF6',paddingHorizontal:14,paddingVertical:8,borderRadius:8,flexDirection:'row',gap:4,alignItems:'center'}}
                    onPress={() => handleInsertAI('replace')}>
                    <Ionicons name="swap-horizontal" size={14} color="#fff"/><Text style={{fontSize:11,fontWeight:'700',color:'#fff'}}>Replace All</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={{backgroundColor:'#10B981',paddingHorizontal:14,paddingVertical:8,borderRadius:8,flexDirection:'row',gap:4,alignItems:'center'}}
                    onPress={() => handleInsertAI('append')}>
                    <Ionicons name="arrow-down" size={14} color="#fff"/><Text style={{fontSize:11,fontWeight:'700',color:'#fff'}}>Append</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={{backgroundColor:'#3B82F6',paddingHorizontal:14,paddingVertical:8,borderRadius:8,flexDirection:'row',gap:4,alignItems:'center'}}
                    onPress={() => handleInsertAI('prepend')}>
                    <Ionicons name="arrow-up" size={14} color="#fff"/><Text style={{fontSize:11,fontWeight:'700',color:'#fff'}}>Prepend</Text>
                  </TouchableOpacity>
                  {aiMode === 'title' && (
                    <TouchableOpacity style={{backgroundColor:'#F59E0B',paddingHorizontal:14,paddingVertical:8,borderRadius:8,flexDirection:'row',gap:4,alignItems:'center'}}
                      onPress={() => handleInsertAI('title')}>
                      <Ionicons name="text" size={14} color="#fff"/><Text style={{fontSize:11,fontWeight:'700',color:'#fff'}}>Use as Title</Text>
                    </TouchableOpacity>
                  )}
                  {aiMode === 'seo' && (
                    <TouchableOpacity style={{backgroundColor:'#0891B2',paddingHorizontal:14,paddingVertical:8,borderRadius:8,flexDirection:'row',gap:4,alignItems:'center'}}
                      onPress={() => handleInsertAI('meta')}>
                      <Ionicons name="search" size={14} color="#fff"/><Text style={{fontSize:11,fontWeight:'700',color:'#fff'}}>Apply SEO</Text>
                    </TouchableOpacity>
                  )}
                  {aiMode === 'summarize' && (
                    <TouchableOpacity style={{backgroundColor:'#EC4899',paddingHorizontal:14,paddingVertical:8,borderRadius:8,flexDirection:'row',gap:4,alignItems:'center'}}
                      onPress={() => handleInsertAI('excerpt')}>
                      <Ionicons name="document-text" size={14} color="#fff"/><Text style={{fontSize:11,fontWeight:'700',color:'#fff'}}>Use as Excerpt</Text>
                    </TouchableOpacity>
                  )}
                </View>

                {/* Copy Result */}
                <TouchableOpacity style={{marginTop:12,flexDirection:'row',alignItems:'center',justifyContent:'center',gap:6,paddingVertical:8}}
                  onPress={async () => { try { await Clipboard.setStringAsync(aiResult); Alert.alert('Copied','AI content copied'); } catch {} }}>
                  <Ionicons name="copy-outline" size={14} color="#94A3B8"/><Text style={{fontSize:11,color:'#94A3B8'}}>Copy to clipboard</Text>
                </TouchableOpacity>
              
      {/* AI Assistant FAB */}
      <TouchableOpacity 
        onPress={() => setShowAIChat(true)} 
        style={{ position:'absolute', bottom:20, right:20, width:56, height:56, borderRadius:28, backgroundColor:'#6366F1', alignItems:'center', justifyContent:'center', elevation:8, shadowColor:'#6366F1', shadowOffset:{width:0,height:4}, shadowOpacity:0.3, shadowRadius:8, zIndex:999 }}>
        <MaterialCommunityIcons name="robot" size={24} color="#fff" />
      </TouchableOpacity>
      <AIChatWidget screenName="BlogEditorScreen" visible={showAIChat} onClose={() => setShowAIChat(false)} title="Content AI" extraContext={{}} />

    </View>
            ) : null}
            <View style={{height:40}}/>
          </ScrollView>
        </SafeAreaView>
      </Modal>

    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#4A90E2',
  },
  header: {
    backgroundColor: '#4A90E2',
    paddingVertical: 15,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerTitle: {
    color: '#fff',
    fontSize: 20,
    fontWeight: 'bold',
    flex: 1,
    textAlign: 'center',
  },
  tabContainer: {
    backgroundColor: '#fff',
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e1e1e1',
  },
  tabButton: {
    flex: 1,
    paddingVertical: 15,
    alignItems: 'center',
  },
  activeTabButton: {
    borderBottomWidth: 2,
    borderBottomColor: '#0e4e96',
  },
  tabText: {
    color: '#666',
    fontWeight: '500',
  },
  activeTabText: {
    color: '#0e4e96',
    fontWeight: 'bold',
  },
  contentContainer: {
    flex: 1,
    padding: 15,
  },
  tabContent: {
    marginBottom: 20,
  },
  inputContainer: {
    marginBottom: 15,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#333',
    marginBottom: 8,
  },
  titleInput: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    color: '#333',
  },
  categoryScroll: {
    marginBottom: 5,
  },
  categoryButton: {
    backgroundColor: '#fff',
    paddingVertical: 8,
    paddingHorizontal: 15,
    borderRadius: 20,
    marginRight: 10,
    borderWidth: 1,
    borderColor: '#ddd',
  },
  selectedCategoryButton: {
    backgroundColor: '#0e4e96',
    borderColor: '#0e4e96',
  },
  categoryButtonText: {
    fontSize: 14,
    color: '#666',
  },
  selectedCategoryButtonText: {
    color: '#fff',
  },
  featuredImageContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  featuredImagePreview: {
    flex: 1,
    backgroundColor: '#f0f7ff',
    borderRadius: 8,
    padding: 15,
    alignItems: 'center',
    justifyContent: 'center',
    height: 100,
  },
  featuredImageText: {
    color: '#0e4e96',
    marginTop: 8,
    fontWeight: '500',
  },
  changeImageButton: {
    backgroundColor: '#0e4e96',
    paddingVertical: 8,
    paddingHorizontal: 15,
    borderRadius: 8,
    marginLeft: 10,
  },
  changeImageButtonText: {
    color: '#fff',
    fontWeight: '500',
  },
  selectImageButton: {
    backgroundColor: '#f0f7ff',
    borderRadius: 8,
    padding: 15,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
  },
  selectImageButtonText: {
    color: '#0e4e96',
    fontWeight: '500',
    marginLeft: 8,
  },
  formattingToolbar: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 10,
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  formatOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    marginRight: 10,
    backgroundColor: '#f5f5f5',
  },
  selectedFormatOption: {
    backgroundColor: '#0e4e96',
  },
  formatOptionText: {
    fontSize: 14,
    color: '#666',
    marginLeft: 6,
  },
  selectedFormatOptionText: {
    color: '#fff',
  },
  colorPickerWrapper: {
    marginTop: 10,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
  },
  colorPickerContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  colorOption: {
    width: 30,
    height: 30,
    borderRadius: 15,
    marginRight: 10,
    marginBottom: 5,
  },
  selectedColorOption: {
    borderWidth: 2,
    borderColor: '#333',
  },
  contentInput: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    minHeight: 300,
    fontSize: 14,
    color: '#333',
    textAlignVertical: 'top',
  },
  tagsInputContainer: {
    flexDirection: 'row',
    marginBottom: 10,
  },
  tagInput: {
    flex: 1,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    color: '#333',
    marginRight: 10,
  },
  addTagButton: {
    backgroundColor: '#0e4e96',
    width: 44,
    height: 44,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  tag: {
    backgroundColor: '#f0f7ff',
    borderRadius: 20,
    paddingVertical: 6,
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 8,
    marginBottom: 8,
  },
  tagText: {
    color: '#0e4e96',
    fontSize: 14,
    marginRight: 5,
  },
  removeTagButton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: '#e1e1e1',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyPreviewContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 30,
  },
  emptyPreviewText: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    marginTop: 15,
    marginBottom: 20,
  },
  emptyPreviewButton: {
    backgroundColor: '#0e4e96',
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 8,
  },
  emptyPreviewButtonText: {
    color: '#fff',
    fontWeight: 'bold',
  },
  previewContainer: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  previewImagePlaceholder: {
    backgroundColor: '#f0f7ff',
    borderRadius: 8,
    padding: 15,
    alignItems: 'center',
    justifyContent: 'center',
    height: 150,
    marginBottom: 15,
  },
  previewImageText: {
    color: '#0e4e96',
    marginTop: 8,
    fontWeight: '500',
  },
  previewTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 10,
  },
  previewMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 15,
  },
  previewCategoryBadge: {
    backgroundColor: '#0e4e96',
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 15,
    marginRight: 10,
  },
  previewCategoryText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '500',
  },
  previewDate: {
    color: '#666',
    fontSize: 14,
  },
  previewContent: {
    fontSize: 16,
    color: '#333',
    lineHeight: 24,
    marginBottom: 15,
  },
  previewTagsContainer: {
    marginTop: 15,
    paddingTop: 15,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
  },
  previewTagsLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#333',
    marginBottom: 8,
  },
  previewTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  previewTag: {
    backgroundColor: '#f0f7ff',
    borderRadius: 20,
    paddingVertical: 6,
    paddingHorizontal: 12,
    marginRight: 8,
    marginBottom: 8,
  },
  previewTagText: {
    color: '#0e4e96',
    fontSize: 14,
  },
  settingsSection: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 15,
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  settingsSectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 15,
  },
  settingItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  settingInfo: {
    flex: 1,
  },
  settingLabel: {
    fontSize: 14,
    color: '#666',
    marginBottom: 4,
  },
  settingValue: {
    fontSize: 14,
    fontWeight: '500',
    color: '#333',
  },
  settingActionText: {
    color: '#0e4e96',
    fontWeight: '500',
  },
  statusBadge: {
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 15,
  },
  publishedBadge: {
    backgroundColor: '#2ecc71',
  },
  draftBadge: {
    backgroundColor: '#f39c12',
  },
  statusBadgeText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '500',
  },
  settingInput: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    color: '#333',
  },
  textArea: {
    height: 80,
    textAlignVertical: 'top',
  },
  socialSharingButton: {
    backgroundColor: '#0077B5', // LinkedIn blue
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderRadius: 8,
    marginBottom: 10,
  },
  twitterButton: {
    backgroundColor: '#1DA1F2', // Twitter blue
  },
  facebookButton: {
    backgroundColor: '#4267B2', // Facebook blue
  },
  socialSharingButtonText: {
    color: '#fff',
    fontWeight: 'bold',
    marginLeft: 8,
  },
  actionButtonsContainer: {
    flexDirection: 'row',
    padding: 15,
    backgroundColor: '#fff',
    borderTopWidth: 1,
    borderTopColor: '#e1e1e1',
  },
  saveButton: {
    flex: 1,
    backgroundColor: '#6c757d',
    paddingVertical: 12,
    borderRadius: 8,
    marginRight: 10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  saveButtonText: {
    color: '#fff',
    fontWeight: 'bold',
    marginLeft: 8,
  },
  publishButton: {
    flex: 1,
    backgroundColor: '#0e4e96',
    paddingVertical: 12,
    borderRadius: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  publishButtonText: {
    color: '#fff',
    fontWeight: 'bold',
    marginLeft: 8,
  },
});

export default BlogEditorScreen;
